import { AUTH_ERROR_CODES, AUTH_ERROR_KINDS, type AuthError } from '@wheely/web-auth-lib/core';
import type { IntlShape } from 'react-intl';

/**
 * A short, human message for an auth flow error. Codes the user can act on get
 * their own text; everything else falls back by kind.
 */
export const getAuthErrorText = (intl: IntlShape, error: AuthError): string => {
  switch (error.code) {
    case AUTH_ERROR_CODES.INVALID_OTP:
    case AUTH_ERROR_CODES.OTP_EXPIRED:
      return intl.formatMessage({
        defaultMessage: 'Incorrect or expired code. Please try again.',
        id: 'login_error_invalid_code',
      });
    case AUTH_ERROR_CODES.EXCEED_OTP_ATTEMPTS:
      return intl.formatMessage({
        defaultMessage: 'Too many incorrect attempts. Request a new code.',
        id: 'login_error_attempts_exhausted',
      });
    case AUTH_ERROR_CODES.OTP_NOT_SENT:
      return intl.formatMessage({
        defaultMessage: 'The code was not sent. Please try again.',
        id: 'login_error_code_not_sent',
      });
    case AUTH_ERROR_CODES.EMAIL_IDENTIFIER_CONFLICT:
      return intl.formatMessage({
        defaultMessage: 'This email is already linked to another account.',
        id: 'login_error_email_in_use',
      });
    case AUTH_ERROR_CODES.PHONE_IDENTIFIER_CONFLICT:
      return intl.formatMessage({
        defaultMessage: 'This phone number is already linked to another account.',
        id: 'login_error_phone_in_use',
      });
    case AUTH_ERROR_CODES.EMAIL_BLACKLISTED:
      return intl.formatMessage({
        defaultMessage: 'This email address cannot be used. Please enter another one.',
        id: 'login_error_email_blacklisted',
      });
    default:
      break;
  }

  switch (error.kind) {
    case AUTH_ERROR_KINDS.RATE_LIMIT:
      // The gateway localizes its throttling text by Accept-Language and says
      // more than ours (why, and how long to wait), so it wins when present.
      return (
        error.message ||
        intl.formatMessage({
          defaultMessage: 'Too many requests. Please try again later.',
          id: 'login_error_rate_limit',
        })
      );
    case AUTH_ERROR_KINDS.VALIDATION:
      return intl.formatMessage({
        defaultMessage: 'Please check the value and try again.',
        id: 'login_error_validation',
      });
    default:
      return intl.formatMessage({
        defaultMessage: 'Something went wrong. Please try again later.',
        id: 'login_error_generic',
      });
  }
};
